import {
  breakpointSizes,
  ButtonDesigns,
  ButtonTypes,
  getBreakpointDown,
  getFontCss,
  HoverableLink,
  styleConstants,
  themeConstants,
} from '@paywithextend/components'
import React from 'react'
import { useDispatch } from 'react-redux'
import { createSetIsToastOpen } from 'src/app/Toast/redux/toast.actions'
import { RemoveTransactionSuccessToastMessage } from 'src/app/Toast/Toast'
import Button from 'src/components/Button/Button'
import Popup from 'src/components/Popup/Popup'
import Trns from 'src/localization/Trns'
import { TransactionDetailModel } from 'src/models/transaction.models'
import { closeModal } from 'src/navigation/Lnk'
import { useDeletePhysicalTransaction } from 'src/queries/transactions.queries'
import { css } from 'styled-components'

interface RemoveTransactionProps {
  transaction: TransactionDetailModel
  closeFunction: (val: boolean) => void
}

const RemoveTransaction: React.FC<RemoveTransactionProps> = (
  props,
): React.ReactElement => {
  const {
    mutate: removeTransaction,
    isPending: loading,
    error,
  } = useDeletePhysicalTransaction()

  const dispatch = useDispatch()

  const onSubmit = (): void => {
    removeTransaction(
      { transactionId: props.transaction.id },
      {
        onSuccess: () => {
          dispatch(closeModal())
          dispatch(
            createSetIsToastOpen(true, {
              toastMessage: <RemoveTransactionSuccessToastMessage />,
            }),
          )
        },
      },
    )
  }

  return (
    <Popup
      onClose={(): void => props.closeFunction(false)}
      css={css`
        ${getBreakpointDown(breakpointSizes.XSM)(css`
          width: 100%;
        `)}
      `}
    >
      <div
        css={css`
          display: flex;
          flex-direction: column;
          align-items: flex-start;
          gap: 32px;
          margin-bottom: 20px;
        `}
      >
        <span
          css={css`
            ${getFontCss(
              32,
              themeConstants.colors.font_primary,
              styleConstants.fontFamilies.medium,
            )};
          `}
        >
          <Trns source={'Remove Transaction'} />
        </span>
        <span
          css={css`
            ${getFontCss(
              16,
              themeConstants.colors.font_primary,
              styleConstants.fontFamilies.regular,
            )};
          `}
        >
          <Trns
            source={
              "It looks like you're about to remove the following transaction. This action cannot be undone. Please double-check the transaction before proceeding."
            }
          />
        </span>
      </div>
      <div
        css={css`
          width: 100%;
          display: flex;
          flex-direction: row;
          padding: 20px;
          border: 1px ${styleConstants.colors.gray_medium} solid;
          border-radius: ${styleConstants.borderRadius.box};
          background-color: ${styleConstants.colors.gray_light};
          gap: 40px;
        `}
      >
        <div
          css={css`
            display: flex;
            flex-direction: row;
            width: calc(55% - 40px);
            gap: 20px;
          `}
        >
          {/* date */}
          <div>
            {props.transaction.dateClearedString ||
              props.transaction.dateString}
          </div>
          {/* merchant */}
          <div
            css={css`
              display: flex;
              flex-direction: column;
            `}
          >
            <span>{props.transaction.merchantNameOrUnknown}</span>
            <span
              css={css`
                ${getFontCss(
                  12,
                  styleConstants.colors.gray_darkest,
                  styleConstants.fontFamilies.regular,
                )};
                margin-top: 4px;
              `}
            >
              {props.transaction.mccGroupIfKnownMerchant}
            </span>
          </div>
        </div>
        <div
          css={css`
            display: flex;
            flex-direction: row;
            gap: 20px;
            justify-content: space-between;
            flex: 1;
          `}
        >
          {/* card user */}
          <div>{props.transaction.nameOnCard}</div>
          {/* amount */}
          <div>{props.transaction.clearingOrAuthLabel(false)}</div>
        </div>
      </div>
      {error && (
        <div
          css={css`
            ${getFontCss(
              14,
              styleConstants.colors.red,
              styleConstants.fontFamilies.regular,
            )}
            margin-top: 32px;
          `}
        >
          {error.error}
        </div>
      )}
      <div
        css={css`
          display: flex;
          align-items: center;
          margin-top: 32px;
          gap: 20px;
        `}
      >
        <Button
          type={ButtonTypes.submit}
          design={ButtonDesigns.designRed}
          big
          text={<Trns source={'Confirm and Remove Transaction'} />}
          onClick={onSubmit}
          loading={loading}
        />
        <HoverableLink
          onClick={(): void => {
            if (!loading) props.closeFunction(false)
          }}
          ariaLabelSource={'Cancel'}
          hoverUnderline={!loading}
        >
          <Trns source={'Cancel'} />
        </HoverableLink>
      </div>
    </Popup>
  )
}

export default RemoveTransaction
